import React, { useState, useRef, useEffect, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import ReactDatePicker, { registerLocale } from 'react-datepicker-isobar';
import utils from '@ehi/global-marketing-interface/utils';
import { trapFocus } from 'utils/trapFocus';
import { scrollLock } from 'utils/scrollLock';
import localization from 'utils/localization';
import { isInViewport } from 'utils/accessibility/viewport';
import { isDateRestrictedByContract } from 'utils/reservation';
import { formatDateTimeStampAndReturnDateTimeObj } from 'utils/date';
import RESERVATIONS from 'constants/reservations';
import cn from 'classnames';
import FieldControl from 'components/Form/FieldControl';
import useClickOutside from 'hooks/useClickOutside';
import CustomContainer from './CustomContainer';
import CustomInput from './CustomInput';

/**
 * DatePicker Description:
 * implementation of react-datepicker into our app
 * uses a button as a custom input
 * wrapped in FieldControl to behave and appear like our other fields
 * handles one half of a date range
 *
 * @param {object} props - React Props
 * @param {object} props.breakpoint
 * @param {*} props.fieldProps - any props to get applied to the Final Form FieldControl
 * @param {Date} props.startDate - beginning of the date range
 * @param {Date} props.endDate - end of the date range
 * @param {function} props.onChange - function fired when any date value changes
 * @param {Date} props.selected - the date that THIS picker is choosing
 * @param {Date} props.minDate
 * @param {Date} props.maxDate
 * @param {number} [props.monthsShown=2] - number of months that will appear in the dropdown at one time
 * @param {boolean} props.selectsStart - will determine that this picker handles the start of the date range
 * @param {boolean} props.selectsEnd - will determine that this picker handles the end of the date range
 * @param {string} props.helpText - non-error text to provide instruction to the user
 * @param {string} props.errorText - describes what went wrong
 * @param {object|boolean} props.eligibility - branch location hours/days from GMI that gets used to determine disabled/enabled days on the calendar
 * @param {string} props.label - field label
 * @param {boolean} props.rightAlignDatepicker - positions the datepicker to the right edge
 * @param {boolean} props.timeAndDatePickerFullHeight
 * @param {boolean} props.isUnavailableLocationContent
 *
 * @return {JSX}
 */

const DatePicker = forwardRef((props, ref) => {
  const {
    input,
    meta,
    selected,
    breakpoint,
    onBlur,
    onChange,
    onFocus,
    showGuidedError,
    startDate,
    endDate,
    selectsStart,
    contractRestrictions,
    isUnavailableLocationContent,
    maxDate,
    eligibility,
    monthsShown,
    helpText,
    guided,
    renderGuidedError,
    label,
    rightAlignDatepicker,
    timeAndDatePickerFullHeight,
    asterisk,
    id,
    isRental,
  } = props;

  const { isMobile } = breakpoint;

  const [inline] = useState(false);
  const [closeDatePickerContainer, setCloseDatePickerContainer] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // since we're generating our own locale, it doesn't matter what we call this
    // just make sure we use the 'alamo' locale name on the <Datepicker locale='alamo' />
    registerLocale('alamo', localization.buildDatePickerLocale('alamo'));
  }, []);

  const prevMetaRef = useRef(meta);
  const prevInputRef = useRef(input);
  const datePickerRef = useRef(null);
  const customInputRef = useRef(null);
  const calendarContainerRef = useRef(null);
  const clickOutsideRef = useRef(null);

  const cleanUpScrollLock = useCallback(() => {
    if (breakpoint.isMobile) {
      scrollLock({ toggleValue: false });
    }
  }, [breakpoint]);

  const handleChange = (date, e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    if (typeof onChange === 'function') {
      onChange(date);
    }

    // Focus the button/input after selecting a date except on mobile
    if (!breakpoint.isMobile && customInputRef.current) {
      customInputRef.current.focus();
    }

    cleanUpScrollLock();
    setCloseDatePickerContainer(true);
  };

  useEffect(() => {
    const prevMeta = prevMetaRef.current;
    // const prevInput = prevInputRef.current;

    // Check if isFocusedActive changed from true to false
    if (prevMeta?.data?.isFocusedActive && !meta?.data?.isFocusedActive) {
      input.onBlur();
    }
    // If input value is empty but there is a selected date, fire input change (normally on load value)
    // If input value is empty but selected exists, call handleChange
    if (!input?.value && selected) {
      handleChange(selected);
    }

    // Update refs for next render
    prevMetaRef.current = meta;
    prevInputRef.current = input;
  }, [meta, input, selected]);

  const scrollToSelectedMonth = useCallback(() => {
    if (breakpoint.isMobile) {
      let month = document
        .querySelector('.react-datepicker__day--selected')
        ?.closest('.react-datepicker__month-container');
      if (!month) {
        month = document
          .querySelector('.react-datepicker__day--range-start')
          ?.closest('.react-datepicker__month-container');
      }
      if (calendarContainerRef.current?.parentElement && month?.offsetTop) {
        calendarContainerRef.current.parentElement.scrollTop = month.offsetTop;
      }
    }
  }, [breakpoint]);

  const setCalendarContainerRef = useCallback(
    (el) => {
      calendarContainerRef.current = el;
      if (el) {
        scrollToSelectedMonth();
      }
    },
    [scrollToSelectedMonth]
  );

  const handleInputClick = () => {
    // If datepicker button is pressed while calendar is already open, closes the calendar.
    // Needs to be in a setTimeout to override ReactDatePicker automatically opening on every click
    if (datePickerRef.current?.state?.open && !meta?.data?.isFocusedActive) {
      setTimeout(() => {
        datePickerRef.current?.setOpen(false);
      }, 0);
    }

    // Need to blur the input after popping open the calendar in mobile
    // Otherwise first click on the calendar doesn't register
    if (breakpoint.isMobile) {
      if (customInputRef.current) {
        customInputRef.current.blur();
      }
      scrollLock();
    }

    // Resetting the state value to false on every click of pickup and return date
    setCloseDatePickerContainer(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    if (datePickerRef.current) {
      datePickerRef.current.setOpen(false);
    }
    cleanUpScrollLock();
  };

  const handleScrollIntoView = (elem) => {
    if (elem && !isInViewport(elem, 100)) {
      elem.scrollIntoView({ block: 'center' });
    }
  };

  const handleFocus = (event) => {
    const isCustomInputEventTarget = event.nativeEvent.target === customInputRef.current;
    if (isCustomInputEventTarget && typeof onFocus === 'function') {
      onFocus(event);
    }

    handleScrollIntoView(event.target);
    // Trap Focus when user has datepicker open
    trapFocus(
      '.react-datepicker__custom-wrapper',
      '.react-datepicker__close, .react-datepicker__day[tabindex="0"]:not(.react-datepicker__day--outside-month)',
      (el) => window.innerWidth < 1279 && el
    );

    // In some cases both click and focus events are triggered together and can one reverse the other (datepicker blink).
    // To fix that we have to wait for click event to finish, if triggered, and then check for datepicker open state and
    // also if the field is FocusedActive by the guided flow.
    // Also, checks for errors on focus so that the datepicker is properly opened when attempting to submit
    // a form with an empty field on the BW (AWR-1945)
    const isClosedAndFocused = !datePickerRef.current?.state?.open && meta.data.isFocusedActive;

    // Checking if custom input is the target here prevents misfires when closing the datepicker on mobile
    const hasErrorAndIsTargeted = isCustomInputEventTarget && showGuidedError;

    if (isClosedAndFocused || hasErrorAndIsTargeted) {
      datePickerRef.current?.setOpen(true);

      // Need to blur the input after popping open the calendar in mobile
      // otherwise first click on the calendar doesn't register
      // (only matters when popping up when there's a submit error)
      if (breakpoint.isMobile && customInputRef.current) {
        customInputRef.current.blur();
        scrollLock();
      }
    }

    // In some cases even after selecting the date , datepicker remains open
    // To fix that we are checking the below values to that have selected values
    // set value false to datePickerRef setOPen function ,when the below condition is true
    if ((startDate || endDate) && selected && isClosedAndFocused && closeDatePickerContainer) {
      datePickerRef.current?.setOpen(false);
    }
  };

  const handleBlur = useCallback(
    (e) => {
      const isPickerClosed = !datePickerRef.current?.state?.open;
      const isTargetInput = e.nativeEvent.target === customInputRef.current;

      if (isPickerClosed && isTargetInput && typeof onBlur === 'function') {
        onBlur(e);
      }
    },
    [onBlur]
  );

  const handleKeyDown = useCallback(
    // custom inputs aren't regaining focus if you escape out of the popper
    (e) => {
      if (e.key === 'Escape' && datePickerRef.current?.state?.open) {
        e.stopPropagation(); // prevent Esc from closing modals, etc. we don't want "Escape" to trigger on any parent elements, such as modals
        cleanUpScrollLock();
        customInputRef.current?.focus();
      }
    },
    [cleanUpScrollLock]
  );

  const formatWeekDay = (day) => day[0];

  const getIsDateRestrictedByContract = (date) =>
    contractRestrictions
      ? isDateRestrictedByContract(
          selectsStart ? RESERVATIONS.EXCHANGE_TYPE_PICKUP : RESERVATIONS.EXCHANGE_TYPE_RETURN,
          formatDateTimeStampAndReturnDateTimeObj(date),
          contractRestrictions
        )
      : true;

  const renderDayContents = (day, date) => (
    <>
      {!getIsDateRestrictedByContract(date) && <span className='react-datepicker__day-number-tag' />}
      <span className='react-datepicker__day-number'>{utils.getDateTimeObjFromTs(date).date()}</span>
    </>
  );

  const renderCustomContainer = (customProps) => (containerProps) =>
    <CustomContainer {...customProps} {...containerProps} ref={setCalendarContainerRef} />;

  // Checking excludeDates for pickup date from 'AFTER' because, Counter Hours should not override the PickUp Hours.
  const getExcludedDaysForPickup = (eligibilityData) =>
    eligibilityData
      ? Object.keys(eligibilityData).reduce((acc, curr) => {
          if (!eligibilityData[curr]?.AFTER) {
            if (eligibilityData[curr].STANDARD.closed) {
              acc.push(utils.getDateTimeObjFromTs(curr).toDate());
            }
          } else if (eligibilityData[curr]?.AFTER?.closed) {
            acc.push(utils.getDateTimeObjFromTs(curr).toDate());
          }
          return acc;
        }, [])
      : [];

  // Checking excludeDates for retun date from 'DROP' because, Counter Hours should not override the Drop Hours.
  const getExcludedDaysForReturn = (eligibilityData) =>
    eligibilityData
      ? Object.keys(eligibilityData).reduce((acc, curr) => {
          if (!eligibilityData[curr]?.DROP) {
            if (eligibilityData[curr].STANDARD.closed) {
              acc.push(utils.getDateTimeObjFromTs(curr).toDate());
            }
          } else if (eligibilityData[curr]?.DROP?.closed) {
            acc.push(utils.getDateTimeObjFromTs(curr).toDate());
          }
          return acc;
        }, [])
      : [];

  const setDayClassNames = (date) =>
    cn({
      'react-datepicker__day--range-start': startDate && date.toISOString() === startDate.toISOString(),
      'react-datepicker__day--range-end': endDate && date.toISOString() === endDate.toISOString(),
      'react-datepicker__day--special': !getIsDateRestrictedByContract(date),
      'react-datepicker__day--after-max-date': maxDate && date > maxDate,
    });

  const getMonthsShown = (defaultMonthsShown) => {
    if (breakpoint.isMobile) {
      return 13;
    }
    if (breakpoint.isTablet) {
      return 1;
    }
    return defaultMonthsShown;
  };

  const handleCalendarOpen = () => {
    setIsOpen(true);
    const today = document.getElementsByClassName('react-datepicker__day--today')[0];
    const dayRangeStart = document.getElementsByClassName('react-datepicker__day--range-start')[0];
    const dayRangeEnd = document.getElementsByClassName('react-datepicker__day--range-end')[0];
    const calendarOpen = document.getElementsByClassName('react-datepicker__month-container')[0];

    if (isUnavailableLocationContent) {
      calendarOpen.classList.add('react-datepicker__datepicker-height');
    }

    if (dayRangeStart) {
      // calendar has been opened in a different month
      dayRangeStart.focus();
      handleScrollIntoView(dayRangeStart);
    } else if (today) {
      today.focus();
      handleScrollIntoView(today);
    } else {
      dayRangeEnd?.focus();
      dayRangeEnd && handleScrollIntoView(dayRangeEnd);
    }

    // Accessibility - The aria-pressed attribute will be reported to assistive technology users as a selected date.
    dayRangeStart?.setAttribute('aria-pressed', true);
    dayRangeEnd?.setAttribute('aria-pressed', true);
  };

  const excludeDates =
    id === 'pickupDate' ? getExcludedDaysForPickup(eligibility) : getExcludedDaysForReturn(eligibility);

  const handleClickOutside = () => {
    // Delay needed to let date selection finish
    // For some reason, this handleClickOutside was causing issues with selecting a date on progress bar in desktop.
    // Probably some weird interaction with ReactDatePicker which has its own built in clickOutsideFunctionality.
    // Putting it into setTimeout solves this issue
    setTimeout(onBlur);
    if (isOpen) {
      setIsOpen(false);
      cleanUpScrollLock();
      onBlur?.();
    }
  };

  useClickOutside(
    {
      container: clickOutsideRef,
      handler: handleClickOutside,
      listenerConditional: isOpen,
    },
    [handleClickOutside, isOpen]
  );

  return (
    <div ref={ref}>
      <div className='react-datepicker__outer-wrapper' ref={clickOutsideRef} onBlur={handleBlur} onFocus={handleFocus}>
        <ReactDatePicker
          onCalendarOpen={handleCalendarOpen}
          locale='alamo'
          {...props}
          startDate={startDate}
          endDate={endDate}
          ref={datePickerRef}
          inline={isMobile && inline}
          selected={selected}
          openToDate={selected}
          withPortal={isMobile}
          monthsShown={getMonthsShown(monthsShown)}
          showDisabledMonthNavigation
          calendarClassName='datePicker'
          onChange={handleChange}
          formatWeekDay={formatWeekDay}
          renderDayContents={renderDayContents}
          calendarContainer={renderCustomContainer({
            meta,
            guided,
            helpText,
            handleClose,
            isMobile,
            showGuidedError,
            renderGuidedError,
            smallWrapper: getMonthsShown(monthsShown) === 1,
            timeAndDatePickerFullHeight,
            contractRestrictions,
            isRental,
          })}
          dateFormat='yyyy-MM-dd' // this format helps the datepicker match the format above. intentionally lowercase "y" and "d"
          excludeDates={excludeDates}
          onKeyDown={handleKeyDown}
          customInput={
            <CustomInput
              setCustomInputRef={customInputRef}
              onInputClick={handleInputClick}
              label={label}
              datePickerRef={datePickerRef}
              selectedProp={selected}
              asterisk={asterisk}
            />
          }
          preventOpenOnFocus={true} // prevent lib focus handling in order to use our custom logic
          dayClassName={setDayClassNames}
          popperPlacement={rightAlignDatepicker ? 'bottom-end' : 'bottom-start'}
          popperModifiers={{
            flip: {
              enabled: false,
            },
          }}
        />
      </div>
    </div>
  );
});

DatePicker.propTypes = {
  // container props:
  breakpoint: PropTypes.object,
  // parent props:
  fieldProps: PropTypes.any,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  selected: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  monthsShown: PropTypes.number,
  selectsStart: PropTypes.bool,
  selectsEnd: PropTypes.bool,
  helpText: PropTypes.string,
  errorText: PropTypes.string,
  eligibility: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  label: PropTypes.string,
  rightAlignDatepicker: PropTypes.bool,
  timeAndDatePickerFullHeight: PropTypes.bool,
  isRental: PropTypes.bool,
};

DatePicker.defaultProps = {
  monthsShown: 2,
};

const DatePickerField = (props) => (
  <FieldControl {...props} dropdown type='datepicker'>
    <DatePicker />
  </FieldControl>
);

export default DatePickerField;
